/* SalesBookCall.tsx */

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import moment from "moment";
import "moment-timezone";

import {
  Button,
  FlashMessage,
  SelectNew,
} from "../../components/common";
import httpRequest from "../../utils/httpRequest";
import API_URLS from "../../utils/apiUrls";
import { TIME_ZONE_LIST } from "../../utils/appConstants";

import bookCallImage from "../../assets/images/book-call-image.png";
import bookCallImageAfter from "../../assets/images/book-call-after.png";
import { CircularProgress } from "@material-ui/core";
import prevArrow from "../../assets/svg/arrow-left.svg";
import nextArrow from "../../assets/svg/arrow-right.svg";

const CALL_DURATION_OPTIONS = [
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
];

const SalesBookCall: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ saleId?: string }>();

  // saleId can come either from /book-call/:saleId or ?saleId=123
  const searchParams = new URLSearchParams(location.search);
  const saleId =
    params.saleId || searchParams.get("saleId") || "";

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [timeZone, setTimeZone] = useState<any | null>(null);
  const [callDuration, setCallDuration] = useState<any>(
    CALL_DURATION_OPTIONS[0]
  );
const [slots, setSlots] = useState<string[]>([]);

  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [loadingSlots, setLoadingSlots] = useState<boolean>(false);
  const [booking, setBooking] = useState<boolean>(false);

  // pre-select browser timezone if we have it in TIME_ZONE_LIST
  useEffect(() => {
    const browserTz =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const matchedTz =
      TIME_ZONE_LIST.find((t) => t.value === browserTz) ||
      TIME_ZONE_LIST[0];

    setTimeZone(matchedTz);
  }, []);

  // fetch slots whenever date, timezone or duration changes
  useEffect(() => {
    if (!saleId || !timeZone?.value || !callDuration?.value) return;
    fetchSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleId, selectedDate, timeZone, callDuration]);

  // const fetchSlots = async () => {
  //   try {
  //     setLoadingSlots(true);
  //     setSelectedSlot(null);

  //     const payload = {
  //       saleId: parseInt(String(saleId), 10),
  //       date: moment(selectedDate).format("YYYY-MM-DD"),
  //       timezone: timeZone.value,
  //       duration: callDuration.value,
  //     };

  //     // backend: POST /sales/slots
  //     const res = await httpRequest().post(
  //       API_URLS.salesSlots, // <-- define in apiUrls
  //       payload
  //     );

  //     const newSlots: string[] = res.data?.data?.slots || res.data?.slots || [];
  //     setSlots(newSlots);
  //   } catch (error: any) {
  //     FlashMessage(
  //       error?.response?.data?.message || "Unable to load time slots",
  //       "error"
  //     );
  //   } finally {
  //     setLoadingSlots(false);
  //   }
  // };

  const fetchSlots = async () => {
  try {
    setLoadingSlots(true);
    setSelectedSlot(null);

    const payload = {
      saleId: parseInt(String(saleId), 10),
      date: moment(selectedDate).format("YYYY-MM-DD"),
      timezone: timeZone.value,
      duration: callDuration.value,
    };

    const res = await httpRequest().post(
      API_URLS.salesSlots,
      payload
    );

    // backend: data is [{ label, value }]
    const newSlots: string[] = (res.data?.data || []).map(
      (s: any) => s.label // "10:15", "10:30", ...
    );

    setSlots(newSlots);
  } catch (error: any) {
    FlashMessage(
      error?.response?.data?.message || "Unable to load time slots",
      "error"
    );
  } finally {
    setLoadingSlots(false);
  }
};
  const slotEx = (hour: string) => {
    const h = parseInt(hour.split(":")[0], 10);
    return h >= 12 ? "PM" : "AM";
  };

  const slotNumber = (hour: string) => {
    const [hStr, m] = hour.split(":");
    const h = parseInt(hStr, 10);
    if (h === 0) return `12:${m}`;
    if (h === 12) return `12:${m}`;
    if (h > 12) return `${h - 12}:${m}`;
    return hour;
  };

  const handleConfirm = async () => {
    if (!saleId) {
      FlashMessage("Missing sales reference. Please try again.", "error");
      return;
    }
    if (!timeZone?.value) {
      FlashMessage("Please select a time zone.", "error");
      return;
    }
    if (!selectedSlot) {
      FlashMessage("Please select a time slot.", "error");
      return;
    }

    try {
      setBooking(true);

      const startLocal = moment.tz(
        `${moment(selectedDate).format("YYYY-MM-DD")} ${selectedSlot}`,
        "YYYY-MM-DD HH:mm",
        timeZone.value
      );

      const payload = {
        saleId: parseInt(String(saleId), 10),
        timezone: timeZone.value,
        duration_minutes: callDuration.value,
        start_time_utc: startLocal.utc().format(), // ISO string
      };

      // backend: POST /sales/book-call
      const res = await httpRequest().post(
        API_URLS.salesBookCall, // <-- define in apiUrls
        payload
      );

      if (res.data?.status === 200 || res.data?.flag) {
        FlashMessage(
          res.data?.message || "Your call has been scheduled.",
          "success"
        );
        // go back to thank-you or some landing page
        navigate("/contact-us/sales", { replace: true });
      } else {
        FlashMessage(
          res.data?.message || "Unable to schedule the call",
          "error"
        );
      }
    } catch (error: any) {
      FlashMessage(
        error?.response?.data?.message || "Unable to schedule the call",
        "error"
      );
    } finally {
      setBooking(false);
    }
  };

  const handleCancel = () => {
    navigate("/contact-us/sales");
  };

  const renderSlotCard = (slot: string, index: number) => {
    const isSelected = selectedSlot === slot;
    return (
      <div
        key={slot + index}
        className={`slot-card ${isSelected ? "selected" : ""}`}
        onClick={() => setSelectedSlot(slot)}
      >
        <h4>
          {slotNumber(slot)} {slotEx(slot)}
        </h4>
      </div>
    );
  };

  return (
    <div className="sales-book-call-page">
      <div className="sales-book-call-inner">
        {/* LEFT SIDE IMAGE */}
        <div className="sales-book-call-left">
          <img src={bookCallImage} alt="Book a call" />
          <div className="after">
            <img src={bookCallImageAfter} alt="Book a call" />
          </div>
        </div>

        {/* RIGHT SIDE CARD */}
        <div className="sales-book-call-right">
          <div className="book-card">
            <div className="book-card-header">
              <h2>Book a Call</h2>
              <p>
                Please select a 15 or 30-minute slot to connect with our team.
              </p>
            </div>

            {/* Top controls */}
            <div className="book-card-controls">
              <div className="control-group">
                <SelectNew
                  name="timezone"
                  externalLabel={{
                    label: "Timezone",
                  }}
                  className="select-box"
                  options={TIME_ZONE_LIST.map((t) => ({
                    value: t.value,
                    label: t.label,
                  }))}
                  isSearchable={true}
                  value={
                    timeZone
                      ? { value: timeZone.value, label: timeZone.label }
                      : null
                  }
                  onChange={(opt: any) => {
                    setTimeZone(opt);
                  }}
                />
              </div>
              <div className="control-group">
                <SelectNew
                  name="duration"
                  externalLabel={{
                    label: "Call Duration",
                  }}
                  className="select-box"
                  options={CALL_DURATION_OPTIONS}
                  isSearchable={false}
                  value={callDuration}
                  onChange={(opt: any) => setCallDuration(opt)}
                />
              </div>
            </div>

            <div className="book-card-body">
              {/* Calendar */}
              <div className="calendar-column date-content">
                 <div className="calendar-date-wrapper">
                <Calendar
                  minDate={moment().toDate()}
                  onChange={(date: any) => setSelectedDate(date)}
                  value={selectedDate}
                  next2Label={false}
                  prev2Label={false}
                  prevLabel={
                  <span className="custom-prev-label">
                      <img src={prevArrow} alt="prev" />
                    </span>
                  }
                  nextLabel={
                    <span className="custom-prev-label">
                      <img src={nextArrow} alt="next" />
                    </span>
                  }
                />
              </div>
</div>
              {/* Slots */}
              <div className="slots-column">
                <div className="slots-header">
                  {moment(selectedDate).format("ddd, MMM DD, YYYY")}
                </div>
                <div className="slots-list">
                  {loadingSlots ? (
                    <div className="slots-loader">
                      <CircularProgress size={24} />
                    </div>
                  ) : slots.length > 0 ? (
                    slots.map(renderSlotCard)
                  ) : (
                    <div className="no-slots">No slots available</div>
                  )}
                </div>
              </div>
            </div>

            <div className="book-card-footer">
              <span className="info-text">
                A confirmation email will be sent after you book.
              </span>
              <div className="footer-actions">
                <Button
                  type="button"
                  color="secondary"
                  className="book-cancel-btn"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  color="primary"
                  className="book-confirm-btn"
                  onClick={handleConfirm}
                  disabled={!selectedSlot || booking}
                  loading={booking}
                >
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesBookCall;
